import { createContext, useContext, useEffect } from 'react'

import { RoutesWithEventSearchSchema } from './RoutesWithEventSearchSchema'

/**
 * Describes the per-cell "filter by value" capability that a surface (page) provides
 * to the shared events-table cells.
 *
 * - identificationVisits provides a `from` route, so cells render a "Filter by value"
 *   link that mutates the events route URL search params (see `CellAction`).
 * - visitorDetails provides no `from`, so cells render without a filter link; the
 *   visitor list supplies its own filter UI (signal/score/country selects + IP chips).
 *
 * **IMPORTANT**: When using EventsTable-based components, wrap them with
 * `EventsTableActionsProvider` to configure filter actions. Without the provider,
 * the "Filter by value" action will silently be omitted.
 */
export interface EventsTableActions {
  /**
   * The route whose URL search params the "filter by value" cell action mutates.
   * When undefined, cells render no filter action and degrade gracefully.
   */
  from?: RoutesWithEventSearchSchema['fullPath']
  /**
   * Optional in-memory IP include/exclude filter callbacks supplied by surfaces that
   * filter client-side (the visitor event list). When provided, the IP cell renders
   * "include this IP" / "exclude this IP" hover actions instead of a route filter link.
   */
  ipFilter?: {
    onInclude: (ip: string) => void
    onExclude: (ip: string) => void
  }
}

/** Sentinel value to detect missing provider */
const MISSING_PROVIDER_SENTINEL: EventsTableActions = {}

const EventsTableActionsContext = createContext<EventsTableActions>(MISSING_PROVIDER_SENTINEL)

export const EventsTableActionsProvider = EventsTableActionsContext.Provider

/**
 * Resolve the events-table actions for the current surface. Defaults to no actions.
 *
 * Missing providers degrade gracefully to no actions.
 */
export function useEventsTableActions(): EventsTableActions {
  const actions = useContext(EventsTableActionsContext)

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && actions === MISSING_PROVIDER_SENTINEL) {
      // oxlint-disable-next-line no-console
      console.warn(
        '[EventsTable] useEventsTableActions called without an EventsTableActionsProvider. ' +
          'The "Filter by value" action will be hidden. If this is intentional (e.g., visitorDetails), ' +
          'wrap with <EventsTableActionsProvider value={{}}> to silence this warning.'
      )
    }
  }, [actions])

  return actions
}
